import { useState } from 'react';
import type { SIPOCData } from '@acde/shared';
import { HiOutlineBuildingOffice2, HiOutlineInboxArrowDown, HiOutlineCog6Tooth, HiOutlineArrowUpTray, HiOutlineUserGroup, HiOutlineChartBar, HiChevronDown, HiChevronUp, HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import type { IconType } from 'react-icons';
import { exportSIPOCToPDF } from '@/lib/pdfExport';

interface SIPOCTableProps {
  data: SIPOCData;
  className?: string;
  isAnalyzing?: boolean;
  processName?: string;
  sessionId?: string;
}

export function SIPOCTable({ data, className = '', isAnalyzing = false, processName, sessionId }: SIPOCTableProps) {
  const [expandedColumn, setExpandedColumn] = useState<string | null>(null);

  const handleDownloadPDF = () => {
    exportSIPOCToPDF(data, {
      processName: processName || 'Untitled Process',
      sessionId,
      includeTimestamp: true,
    });
  };

  const columns: {
    key: 'suppliers' | 'inputs' | 'process' | 'outputs' | 'customers';
    title: string;
    shortTitle: string;
    Icon: IconType;
    description: string;
    items: unknown[];
    renderItem: (item: unknown, index: number) => React.ReactNode;
  }[] = [
    {
      key: 'suppliers' as const,
      title: 'Suppliers',
      shortTitle: 'S',
      Icon: HiOutlineBuildingOffice2,
      description: 'Who provides the inputs?',
      items: data.suppliers,
      renderItem: (item: unknown) => {
        const supplier = item as typeof data.suppliers[0];
        return (
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{supplier.entity}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
              supplier.type === 'internal' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
            }`}>
              {supplier.type}
            </span>
          </div>
        );
      },
    },
    {
      key: 'inputs' as const,
      title: 'Inputs',
      shortTitle: 'I',
      Icon: HiOutlineInboxArrowDown,
      description: 'What resources are needed?',
      items: data.inputs,
      renderItem: (item: unknown) => {
        const input = item as typeof data.inputs[0];
        return (
          <div>
            <div className="truncate">{input.item}</div>
            {input.source && (
              <div className="text-xs text-muted truncate">From: {input.source}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'process' as const,
      title: 'Process',
      shortTitle: 'P',
      Icon: HiOutlineCog6Tooth,
      description: 'What transformation occurs?',
      items: data.processSteps.map((step, i) => ({ id: `step-${i}`, step })),
      renderItem: (item: unknown, index: number) => {
        const processItem = item as { id: string; step: string };
        return (
          <div className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
              {index + 1}
            </span>
            <span className="line-clamp-2">{processItem.step}</span>
          </div>
        );
      },
    },
    {
      key: 'outputs' as const,
      title: 'Outputs',
      shortTitle: 'O',
      Icon: HiOutlineArrowUpTray,
      description: 'What is produced?',
      items: data.outputs,
      renderItem: (item: unknown) => {
        const output = item as typeof data.outputs[0];
        return (
          <div>
            <div className="truncate">{output.item}</div>
            {output.destination && (
              <div className="text-xs text-muted truncate">To: {output.destination}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'customers' as const,
      title: 'Customers',
      shortTitle: 'C',
      Icon: HiOutlineUserGroup,
      description: 'Who receives the outputs?',
      items: data.customers,
      renderItem: (item: unknown) => {
        const customer = item as typeof data.customers[0];
        return (
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{customer.entity}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
              customer.type === 'internal' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
            }`}>
              {customer.type}
            </span>
          </div>
        );
      },
    },
  ];

  const hasData = columns.some((col) => col.items.length > 0);

  const toggleColumn = (key: string) => {
    setExpandedColumn(expandedColumn === key ? null : key);
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-2 sm:px-3 py-2 bg-user-msg rounded-t-lg border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium text-muted shrink-0">SIPOC Analysis</span>
          {data.processSummary && (
            <span className="text-xs text-foreground truncate hidden sm:inline">
              {data.processSummary}
            </span>
          )}
        </div>
        {hasData && (
          <button
            onClick={handleDownloadPDF}
            className="p-1.5 text-xs text-muted hover:text-foreground hover:bg-card rounded transition-colors flex items-center gap-1"
            title="Download SIPOC as PDF"
          >
            <HiOutlineDocumentArrowDown className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        {!hasData ? (
          <div className="flex items-center justify-center h-full bg-user-msg rounded-b-lg min-h-[200px]">
            <div className="text-center px-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-card flex items-center justify-center ${isAnalyzing ? 'text-primary' : 'text-muted'}`}>
                {isAnalyzing ? (
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <HiOutlineChartBar className="w-6 h-6 sm:w-8 sm:h-8" />
                )}
              </div>
              <div className="text-xs sm:text-sm text-muted">
                {isAnalyzing ? (
                  <>
                    Analyzing conversation...
                    <br />
                    <span className="text-xs">Extracting SIPOC elements</span>
                  </>
                ) : (
                  <>
                    SIPOC data will populate here
                    <br />
                    as you describe the process
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop: Grid layout */}
            <div className="hidden md:grid grid-cols-5 gap-0 h-full">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className="flex flex-col border-r border-border last:border-r-0"
                >
                  {/* Column header */}
                  <div className="p-2 lg:p-3 bg-user-msg border-b border-border text-center">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 mx-auto mb-1 lg:mb-2 rounded-lg bg-card flex items-center justify-center text-primary">
                      <column.Icon className="w-4 h-4 lg:w-5 lg:h-5" />
                    </div>
                    <div className="text-xs lg:text-sm font-medium text-foreground">
                      {column.title}
                    </div>
                    <div className="text-xs text-muted hidden lg:block">
                      {column.description}
                    </div>
                  </div>

                  {/* Column content */}
                  <div className="flex-1 p-1.5 lg:p-2 space-y-1.5 lg:space-y-2 overflow-y-auto bg-card">
                    {column.items.length === 0 ? (
                      <div className="text-xs text-muted text-center py-4">
                        No items
                      </div>
                    ) : (
                      column.items.map((item, index) => (
                        <div
                          key={(item as { id?: string }).id || index}
                          className="p-1.5 lg:p-2 bg-user-msg rounded-lg text-xs lg:text-sm"
                        >
                          {column.renderItem(item, index)}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: Accordion layout */}
            <div className="md:hidden divide-y divide-border">
              {columns.map((column) => (
                <div key={column.key} className="bg-card">
                  {/* Accordion header */}
                  <button
                    onClick={() => toggleColumn(column.key)}
                    className="w-full px-3 py-2.5 flex items-center justify-between bg-user-msg hover:bg-border transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-card flex items-center justify-center text-primary">
                        <column.Icon className="w-4 h-4" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-foreground">
                          {column.title}
                        </div>
                        <div className="text-xs text-muted">
                          {column.items.length} item{column.items.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    {expandedColumn === column.key ? (
                      <HiChevronUp className="w-5 h-5 text-muted" />
                    ) : (
                      <HiChevronDown className="w-5 h-5 text-muted" />
                    )}
                  </button>

                  {/* Accordion content */}
                  {expandedColumn === column.key && (
                    <div className="p-2 space-y-2 animate-fade-in">
                      {column.items.length === 0 ? (
                        <div className="text-xs text-muted text-center py-4">
                          No items yet
                        </div>
                      ) : (
                        column.items.map((item, index) => (
                          <div
                            key={(item as { id?: string }).id || index}
                            className="p-2.5 bg-user-msg rounded-lg text-sm"
                          >
                            {column.renderItem(item, index)}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
